






























































import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';
import {
  Coupon,
  Optional,
  PaywallPlanObject,
  PaywallPlansInfo,
  PlanType,
  User,
} from 'frontend/types';
import PlanCard from 'frontend/views/premium/paywall/PlanCard.vue';
import { Discount } from 'frontend/types/premium';
import {
  isLegacyDirectTraffic,
  isLegacySeasonal,
  isStudentDiscount,
} from 'frontend/utils/discounts';
import LightningSvg from 'frontend/views/premium/paywall/svgs/LightningSvg.vue';
import { safeWindow } from 'frontend/utils/window';

Vue.directive('observe-out-of-view', {
  bind(el, binding) {
    const observer = new IntersectionObserver(
      (entries, ob) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            // The element is out of view
            const callback = binding.value;
            if (callback) {
              callback();
            }
          }
        });
      },
      {
        threshold: [0],
      },
    );

    // @ts-ignore
    el.observer = observer;
    observer.observe(el);
  },
  unbind(el) {
    // @ts-ignore
    el.observer.disconnect();
  },
});

Vue.directive('observe-in-of-view', {
  bind(el, binding) {
    const observer = new IntersectionObserver(
      (entries, ob) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // The element is out of view
            const callback = binding.value;
            if (callback) {
              callback();
            }
          }
        });
      },
      {
        threshold: [0],
      },
    );

    // @ts-ignore
    el.observer = observer;
    observer.observe(el);
  },
  unbind(el) {
    // @ts-ignore
    el.observer.disconnect();
  },
});

@Component({
  components: {
    TieredPlans,
    LightningSvg,
    PlanCard,
  },
  computed: {
    ...mapGetters('globals', ['discount']),
    ...mapGetters('paywall', ['paywallPlans']),
    ...mapGetters('pageHeader', ['coupon']),
    ...mapState('globals', ['user']),
  },
})
export default class TieredPlans extends Vue {
  @Prop(Boolean) private readonly premiumPage!: boolean | undefined;
  private readonly paywallPlans!: PaywallPlansInfo;

  private user!: User;
  private coupon!: Coupon;

  // tslint:disable-next-line:variable-name
  private is_discounted!: boolean;
  private readonly discount!: Optional<Discount>;

  private get directTrafficDiscount(): boolean {
    return isLegacyDirectTraffic(this.discount);
  }

  private get seasonalDiscount(): boolean {
    return isLegacySeasonal(this.discount);
  }

  private get isStudentDiscount(): boolean {
    return isStudentDiscount(this.discount);
  }

  private get mathFoundationsPlanAvailable(): boolean {
    return this.paywallPlans.annualMathFoundationsPlan !== undefined;
  }

  private planType(plan: PaywallPlanObject): PlanType {
    if (plan.is_math_foundations) {
      return PlanType.mathFoundations;
    }
    return PlanType.premium;
  }

  private get annualPlanIncludesReferralDiscount(): boolean {
    const annualPlanIncludesReferralDiscount =
      this.paywallPlans.annualPlan !== undefined &&
      this.paywallPlans.annualPlan?.includes_referral_discount;
    const annualMathPlanIncludesReferralDiscount =
      this.paywallPlans.annualMathFoundationsPlan !== undefined &&
      this.paywallPlans.annualMathFoundationsPlan?.includes_referral_discount;
    return annualMathPlanIncludesReferralDiscount || annualPlanIncludesReferralDiscount;
  }

  private strikeThroughPrice(planType: PlanType): string | null {
    let paywallPlanObject: PaywallPlanObject | undefined;
    if (planType === PlanType.mathFoundations) {
      paywallPlanObject = this.paywallPlans.annualMathFoundationsPlan;
    } else {
      paywallPlanObject = this.paywallPlans.annualPlan;
    }
    if (
      !this.directTrafficDiscount ||
      this.discount?.is_visible ||
      this.annualPlanIncludesReferralDiscount
    ) {
      // fallback to usual behavior
      if (paywallPlanObject?.strikethrough_price) {
        return paywallPlanObject?.strikethrough_price;
      }
    }
    return null;
  }

  private get shouldShowDiscount(): boolean {
    // Referral discount takes precedence
    if (this.annualPlanIncludesReferralDiscount) {
      return true;
    }
    if (this.seasonalDiscount) {
      return true;
    }
    if (this.directTrafficDiscount) {
      return !!this.discount?.is_visible;
    }
    if (this.isStudentDiscount) {
      return true;
    }
    return !!(
      this.is_discounted ||
      (this.paywallPlans.annualPlan && this.paywallPlans.annualPlan?.strikethrough_price)
    );
  }

  private get discountHtml() {
    if (this.annualPlanIncludesReferralDiscount) {
      return 'Referral discount unlocked!';
    }
    if (this.campaignDiscountOverrideHtml) {
      return this.campaignDiscountOverrideHtml;
    }
    if (this.coupon && this.coupon.code_used_name) {
      return `Discount unlocked for <em>${this.coupon.code_used_name}</em>`;
    }
    if (this.user.days_since_signup !== null && this.user.days_since_signup <= 7) {
      return 'Discount unlocked! Thanks for joining Brilliant.';
    }
    return 'Discount unlocked! Thanks for being active on Brilliant.';
  }

  private get campaignDiscountOverrideHtml(): string | null {
    if (this.seasonalDiscount || this.isStudentDiscount) {
      return `${this.discount?.name} discount applied`;
    }
    if (this.directTrafficDiscount) {
      return 'Limited time discount applied';
    }
    return null;
  }

  private handleScroll(): void {
    const stickyCTA = document.getElementById('sticky-cta');
    const win = safeWindow();
    if (stickyCTA && win) {
      if (win.innerWidth <= 700) {
        stickyCTA.style.display = 'flex';
      } else if (win.innerWidth <= 768) {
        stickyCTA.style.display = 'grid';
      }
    }
  }

  private hideStickyFooter(): void {
    const stickyCTA = document.getElementById('sticky-cta');
    if (stickyCTA) {
      stickyCTA.style.display = 'none';
    }
  }

  private scrollToTop(): void {
    const modal = document.getElementById('top');
    const backButton = document.getElementById('back-button');
    if (backButton && this.premiumPage) {
      const yOffset = -100;
      const win = safeWindow();
      if (win) {
        const y = backButton.getBoundingClientRect().top + win.pageYOffset + yOffset;
        win.scrollTo({ top: y, behavior: 'smooth' });
      }
    } else if (modal) {
      modal.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
